import { useState } from 'react';
import { trpc } from '../../lib/trpc';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../ui/Button';
import { ReviewList, ExperienceReviewForm, type ExperienceReviewFormData } from '../reviews';
import { useEmailVerificationGate } from '../../hooks/useEmailVerificationGate';
import { useReviewPhotoUpload } from '../../hooks/useReviewPhotoUpload';

interface ExperienceReviewsProps {
  experienceId: string;
  experienceName: string;
  includesFood: boolean;
}

export const ExperienceReviews = ({ experienceId, experienceName, includesFood }: ExperienceReviewsProps) => {
  const { user } = useAuth();
  const { requireVerified, gateModal } = useEmailVerificationGate();
  const [showReviewForm, setShowReviewForm] = useState(false);
  const utils = trpc.useUtils();

  const {
    uploadPhotos,
    uploadStates: photoUploadStates,
    isUploading: isUploadingPhotos,
    reset: resetPhotoUploads,
  } = useReviewPhotoUpload(experienceId);

  const createReviewMutation = trpc.experienceReview.create.useMutation({
    onSuccess: () => {
      resetPhotoUploads();
      setShowReviewForm(false);
      utils.experienceReview.list.invalidate({ experienceId });
    },
  });

  const handleSubmitReview = async (data: ExperienceReviewFormData) => {
    const imageIds = await uploadPhotos(data.photos);

    await createReviewMutation.mutateAsync({
      experienceId,
      title: data.title,
      body: data.body,
      ratings: data.ratings,
      imageIds,
    });
  };

  const handleWriteReviewClick = () => {
    if (!requireVerified()) return;
    setShowReviewForm(true);
  };

  return (
    <div className="border-t border-gray-200 pt-6 mt-6">
      {gateModal}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-900">Reviews</h2>
        {user && !showReviewForm && (
          <Button variant="primary" onClick={handleWriteReviewClick}>
            Write a Review
          </Button>
        )}
      </div>

      {showReviewForm && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Write a Review for {experienceName}
          </h3>
          {createReviewMutation.error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {createReviewMutation.error.message}
            </div>
          )}
          <ExperienceReviewForm
            onSubmit={handleSubmitReview}
            onCancel={() => setShowReviewForm(false)}
            isSubmitting={createReviewMutation.isPending || isUploadingPhotos}
            photoUploadStates={photoUploadStates}
            includesFood={includesFood}
          />
        </div>
      )}

      <ReviewList entityId={experienceId} reviewType="experience" />
    </div>
  );
};
